import React, {useState, useEffect} from 'react'
import ReactCrop from 'react-image-crop'
import {Button, ButtonGroup} from 'reactstrap'
import 'react-image-crop/dist/ReactCrop.css'
import {
    actionChangeSelectedLayer,
    actionToggleCropMode,
    updateLayerAttribute,
} from '../context/SelectedVariantContext/action'
import Portal from '../../common/Portal'
import {ReactComponent as Close} from '../../../assets/icons/close.svg'
import {ReactComponent as Check} from '../../../assets/icons/check.svg'
import {caculateWidthOfLeftSide} from '../context/SelectedVariantContext/helper'
import {Spinner} from 'reactstrap/lib'

export const CropImage = ({selectedLayer, dispatch, clientRect, cropLocal}) => {
    let headerWidth = window.innerWidth > 1600 ? 70 : 65
    const [updatedLayerState, setUpdatedLayerState] = useState(selectedLayer)
    const [isApplyingCrop, setIsApplyingCrop] = useState(false)

    // Effect to ensure we always have the most recent layer data
    useEffect(() => {
        if (selectedLayer) {
            setUpdatedLayerState(selectedLayer)
        }
    }, [selectedLayer]) /*  */

    const handleCloseCrop = () => {
        dispatch(actionToggleCropMode(false))

        // Ensure we're using the most up-to-date layer data
        const updatedLayer = {...updatedLayerState, isSelected: true}
        dispatch(actionChangeSelectedLayer(updatedLayer))
        const toolbar = document.getElementById('image-toolbar')
        toolbar.style.display = 'flex'
    }

    const handleApplyCrop = () => {
        if (!cropLocal || !selectedLayer?.src) return
        setIsApplyingCrop(true)

        // Support both {width,height} and {w,h}
        const sx = Math.max(0, Math.round(cropLocal.x ?? 0))
        const sy = Math.max(0, Math.round(cropLocal.y ?? 0))

        const canvas = document.createElement('canvas')
        canvas.width = cropLocal.w
        canvas.height = cropLocal.h
        const ctx = canvas.getContext('2d')

        const img = new Image()
        // In case of remote images, try to avoid tainted canvas
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            try {
                // Clamp source rect within image bounds
                const sw = Math.max(1, Math.min(cropLocal.w, img.width - sx))
                const sh = Math.max(1, Math.min(cropLocal.h, img.height - sy))
                ctx.imageSmoothingEnabled = true
                ctx.drawImage(img, cropLocal.x, cropLocal.y, sw, sh, 0, 0, cropLocal.w, cropLocal.h)

                canvas.toBlob((blob) => {
                    setIsApplyingCrop(false)
                    if (!blob) return
                    const croppedImageUrl = URL.createObjectURL(blob)

                    // Recalculate position so the cropped content stays in the same place on canvas
                    const oxOld =
                        (updatedLayerState && typeof updatedLayerState.offsetX === 'number'
                            ? updatedLayerState.offsetX
                            : (updatedLayerState?.width || 0) / 2) || 0
                    const oyOld =
                        (updatedLayerState && typeof updatedLayerState.offsetY === 'number'
                            ? updatedLayerState.offsetY
                            : (updatedLayerState?.height || 0) / 2) || 0

                    const oxNew = (cropLocal.w || 0) / 2
                    const oyNew = (cropLocal.h || 0) / 2

                    // Delta from old local origin to new local origin (crop origin shifted by new offset)
                    const deltaLocalX = (cropLocal.x || 0) + oxNew - oxOld
                    const deltaLocalY = (cropLocal.y || 0) + oyNew - oyOld

                    // Apply current scale and rotation to translate into world coordinates
                    const sX = typeof updatedLayerState.scaleX === 'number' ? updatedLayerState.scaleX : 1
                    const sY = typeof updatedLayerState.scaleY === 'number' ? updatedLayerState.scaleY : 1
                    const rotationRad = ((updatedLayerState.rotation || 0) * Math.PI) / 180

                    const dxScaled = deltaLocalX * sX
                    const dyScaled = deltaLocalY * sY

                    const cos = Math.cos(rotationRad)
                    const sin = Math.sin(rotationRad)
                    const dxWorld = dxScaled * cos - dyScaled * sin
                    const dyWorld = dxScaled * sin + dyScaled * cos

                    const newX = (updatedLayerState.x || 0) + dxWorld
                    const newY = (updatedLayerState.y || 0) + dyWorld

                    const updatedLayer = {
                        ...updatedLayerState,
                        src: croppedImageUrl,
                        width: cropLocal.w,
                        height: cropLocal.h,
                        x: newX,
                        y: newY,
                        offsetX: oxNew,
                        offsetY: oyNew,
                        isSelected: true,
                        cropped_layer: true,
                    }

                    dispatch(updateLayerAttribute(updatedLayer))
                    dispatch(actionToggleCropMode(false))
                })
            } catch (e) {
                console.error('Crop draw failed', e)
                setIsApplyingCrop(false)
            }
        }
        img.onerror = () => {
            console.error('Failed to load image for cropping', selectedLayer.src)
            setIsApplyingCrop(false)
        }
        img.src = selectedLayer.src
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault()
                handleApplyCrop()
            } else if (e.key === 'Escape') {
                e.preventDefault()
                handleCloseCrop()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [handleApplyCrop, handleCloseCrop])

    const widthOfLeftSide = caculateWidthOfLeftSide()

    return (
        <Portal containerId="mockup-overlay">
            <div
                style={{
                    position: 'absolute',
                    top: `${clientRect.y + headerWidth}px`,
                    left: `${clientRect.x + widthOfLeftSide}px`,
                    width: `${clientRect.width}px`,
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        top: '-48px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ButtonGroup style={{backgroundColor: '#fff', minWidth: '215px'}}>
                        <Button
                            outline
                            disabled={isApplyingCrop}
                            style={{color: '#303238', padding: '6px 17px 6px 13px', border: '1px solid rgba(222, 226, 230, 1)', borderRight: 'none'}}
                            onClick={handleCloseCrop}
                        >
                            <Close style={{marginRight: '4px'}} />
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            style={{padding: '6px 22px 6px 18px', display: 'flex', gap: '4px', alignItems: 'center'}}
                            disabled={isApplyingCrop}
                            onClick={handleApplyCrop}
                        >
                            {isApplyingCrop ? <Spinner size="sm" /> : <Check style={{width: '21px', height: '21px'}} />}
                            Apply
                        </Button>
                    </ButtonGroup>
                </div>
            </div>
        </Portal>
    )
}

